import React from 'react';
import { BootScreen } from 'screens/BootScreen';
import { AsyncStorage } from 'services/asyncStorage';

// Types
type ImpersonationActionType = 'SET_MODE';
type ImpersonationAction = { type: ImpersonationActionType; payload: boolean };

type ImpersonationDispatch = (action: ImpersonationAction) => void;

interface ImpersonationState {
  openImpersonation: boolean;
}

const ImpersonationStateContext = React.createContext<ImpersonationState | undefined>(undefined);
const ImpersonationDispatchContext = React.createContext<ImpersonationDispatch | undefined>(
  undefined
);

const defaultState: ImpersonationState = {
  openImpersonation: false,
};

async function getInitialState() {
  try {
    const openImpersonation = (await AsyncStorage.get('impersonateAccessToken')) ? true : false;
    return {
      openImpersonation,
    } as ImpersonationState;
  } catch (error) {
    return defaultState;
  }
}

const sidebarReducer = (state: ImpersonationState, action: ImpersonationAction) => {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, openImpersonation: action.payload };
    default:
      return state;
  }
};

export const ImpersonationProvider: React.FC<React.ReactNode> = ({ children }) => {
  const [data, setData] = React.useState<unknown>();

  React.useEffect(() => {
    getInitialState().then((data) => {
      setData(data);
    });
  }, []);

  if (!data) return <BootScreen />;

  return <Provider initialState={data}>{children}</Provider>;
};

const Provider: React.FC<{ children: React.ReactNode; initialState: unknown }> = ({
  children,
  initialState,
}: any) => {
  const [state, dispatch] = React.useReducer(sidebarReducer, initialState);

  return (
    <ImpersonationStateContext.Provider value={state}>
      <ImpersonationDispatchContext.Provider value={dispatch}>
        {children}
      </ImpersonationDispatchContext.Provider>
    </ImpersonationStateContext.Provider>
  );
};

export const useImpersonationState = () => {
  const context = React.useContext(ImpersonationStateContext);
  if (context === undefined) {
    throw new Error('useImpersonationState must be used within a ImpersonationProvider');
  }
  return context;
};

export const useImpersonationDispatch = () => {
  const context = React.useContext(ImpersonationDispatchContext);
  if (context === undefined) {
    throw new Error('useImpersonationDispatch must be used within a ImpersonationProvider');
  }
  return context;
};
